import React, { createRef, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash.get';
import { Flex, Typography, Box, Button, Card, CardTitle, CardContent, color } from '@kistech/kis-ui';
import NoImagePreview from 'assets/no_image_preview.png';
import { useActions } from 'hooks/useActions';
import { actions as configurationsActions } from 'store/configurations';
import { actions as mediaActions, selectors as mediaSelectors } from 'store/media';
import { isValidImage } from 'utils/validation';
import { MediaTypes, DisabledMediaProps } from '../../../types';

export const DisabledMedia: React.FC<DisabledMediaProps> = (props: DisabledMediaProps) => {
  const { id, configuration } = props.configuration;
  const globalSettings = configuration && configuration.globalSettings;
  const upperDeck = configuration && configuration.globalSettings && configuration.globalSettings.upperDeck;

  const [updateConfiguration, createMedia] = useActions([
    configurationsActions.updateConfiguration,
    mediaActions.createMedia
  ]);
  const { pending, error } = useSelector(mediaSelectors.selectAction);
  const [uploadImageError, setUploadImageError] = useState(error);
  const fileUploadRef = createRef<HTMLInputElement>();

  const handleFileSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file: File = get(e, ['target', 'files', 0]);
    if (file) {
      const err = isValidImage(file);
      if (err) {
        setUploadImageError(err);
      } else {
        setUploadImageError('');
        createMedia({ file, name: file.name, type: MediaTypes.DISABLE_MEDIA, configurationId: id });
      }
    }
  };

  const onMakeActive = () => {
    updateConfiguration({
      id,
      configuration: {
        ...configuration,
        globalSettings: {
          ...globalSettings,
          upperDeck: { ...upperDeck, type: MediaTypes.DISABLE_MEDIA }
        }
      }
    });
  };

  const triggerFileSelector = () => {
    if (fileUploadRef.current) fileUploadRef.current.click();
  };

  return (
    <Flex fullWidth>
      <input
        type="file"
        id="disable-media-file"
        ref={fileUploadRef}
        style={{ display: 'none' }}
        onChange={handleFileSelection}
        accept="image/png, image/jpeg"
        data-testid="disable-media-file"
      />
      <Box width={650}>
        <Card raised hasBorderRadius>
          <CardTitle
            title={
              <Flex direction="row" fullWidth justify="between">
                <Typography size={18} weight={600}>
                  Disable Media
                </Typography>
                {props.previewUrl ? (
                  <>
                    {upperDeck && upperDeck.type === MediaTypes.DISABLE_MEDIA && !pending ? (
                      <Typography size={18} weight={600} color={color.PRIMARY}>
                        ACTIVE
                      </Typography>
                    ) : (
                      <Button size="x-small" appearance="primary" onClick={onMakeActive} isLoading={pending}>
                        Make Active
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    size="x-small"
                    appearance="primary"
                    isLoading={pending}
                    onClick={triggerFileSelector}
                    data-testid="add-image-button"
                  >
                    Add Image
                  </Button>
                )}
              </Flex>
            }
            gutter={5}
          />
          <CardContent>
            <Box>
              {upperDeck && upperDeck.type === MediaTypes.DISABLE_MEDIA ? (
                <Typography size={18}>
                  Upper Deck media has been disabled for this configuration. To enable media, select either
                  &quot;Slideshow&quot; or &quot;Video&quot; from the Type of Media dropdown above and follow the
                  instructions to upload new media.
                </Typography>
              ) : (
                <Typography size={18} weight={400}>
                  Upload a placeholder image to disable slideshow and video media.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
      <Box margin="0 3" width={325}>
        <Card raised hasBorderRadius>
          <CardTitle
            title={
              <Typography size={18} weight={600}>
                Image Preview
              </Typography>
            }
            gutter={5}
          />
          <CardContent>
            <Flex padding="2" align="center" justify="center">
              {props.previewUrl ? (
                <img alt="preview" width={280} height={158} src={props.previewUrl} style={{ objectFit: 'cover' }} />
              ) : (
                <img alt="preview" width={150} height={150} src={NoImagePreview} data-testid="no-image" />
              )}
              <Box height={20} fullWidth />
              {(error || uploadImageError) && <Typography color={color.RED}>{error || uploadImageError}</Typography>}
              {props.previewUrl ? (
                <>
                  <Button
                    size="small"
                    appearance="primary"
                    width={250}
                    isLoading={pending}
                    onClick={triggerFileSelector}
                    style={{ marginTop: '10px' }}
                  >
                    {'Select Different Image'}
                  </Button>
                  <Box fullWidth margin="2 0" />
                  <Button
                    size="small"
                    width={250}
                    appearance="white"
                    onClick={() => props.onDelete(props.activeImage)}
                    isLoading={pending}
                    data-testid="delete-video-button"
                  >
                    Delete
                  </Button>
                </>
              ) : null}
            </Flex>
          </CardContent>
        </Card>
      </Box>
    </Flex>
  );
};
